import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../providers/AuthProvider'
import { REGIONS } from '../components/shared/regions'
import RegionPickerModal from '../components/shared/RegionPickerModal'
import JobDetailModal from '../components/shared/JobDetailModal'
import { getDistanceFromLatLonInKm } from '../utils/geography'

export default function PharmacistJobListPage() {
    const { user, profile } = useAuth()

    const [jobs, setJobs] = useState([])
    const [loading, setLoading] = useState(false)
    const [detailJob, setDetailJob] = useState(null) // For Modal
    const [applying, setApplying] = useState(false)
    const [requestMessage, setRequestMessage] = useState('')

    // Filter State
    const [q, setQ] = useState('')
    const [selectedRegion, setSelectedRegion] = useState('')
    const [selectedDistrict, setSelectedDistrict] = useState('')
    const [selectedDistance, setSelectedDistance] = useState(50) // 50 means 'All' or max
    const [isRegionModalOpen, setIsRegionModalOpen] = useState(false)

    // Load ALL Open Jobs (Sorted by date default)
    const loadJobs = useCallback(async () => {
        setLoading(true)
        try {
            // 1. Fetch Job Posts
            const { data: jobsData, error: jobsError } = await supabase
                .from('job_posts')
                .select('*')
                .eq('status', 'open')
                .order('date', { ascending: true })

            if (jobsError) throw jobsError
            if (!jobsData || jobsData.length === 0) {
                setJobs([])
                return
            }

            // 2. Fetch Addresses from 'profiles' (migrated address)
            // job_posts uses 'created_by' as the author ID.
            const userIds = [...new Set(jobsData.map(j => j.created_by).filter(Boolean))]
            let addressMap = {}

            if (userIds.length > 0) {
                // Now fetching from 'profiles' which is public/shared
                const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('user_id, address')
                    .in('user_id', userIds)

                if (!profileError && profileData) {
                    profileData.forEach(p => {
                        addressMap[p.user_id] = p.address
                    })
                } else {
                    console.warn('Failed to fetch addresses from profiles:', profileError)
                }
            }

            // 3. Merge Address
            const mergedJobs = jobsData.map(job => ({
                ...job,
                // Match job.created_by (the author) to the registration's user_id
                address: addressMap[job.created_by] || job.address
            }))

            setJobs(mergedJobs)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadJobs()
    }, [loadJobs])

    // Filter Logic
    const filteredJobs = useMemo(() => {
        let filtered = jobs
        if (q.trim()) {
            const term = q.trim().toLowerCase()
            filtered = filtered.filter(j =>
                j.title?.toLowerCase().includes(term) ||
                j.pharmacy_name?.toLowerCase().includes(term) ||
                j.location?.toLowerCase().includes(term)
            )
        }
        if (selectedDistance < 50) {
            // Distance Filter
            const userLat = profile?.latitude
            const userLng = profile?.longitude

            if (userLat && userLng) {
                filtered = filtered.filter(j => {
                    if (!j.latitude || !j.longitude) return false
                    const dist = getDistanceFromLatLonInKm(userLat, userLng, j.latitude, j.longitude)
                    return dist <= selectedDistance
                })
            }
        } else if (selectedRegion) {
            // Region Filter (Only if Distance is NOT active/Max)
            filtered = filtered.filter(j => j.region === selectedRegion)
            if (selectedDistrict && selectedDistrict !== '전체 (구)') {
                filtered = filtered.filter(j => j.district === selectedDistrict)
            }
        }
        return filtered
    }, [jobs, q, selectedRegion, selectedDistrict, selectedDistance, profile])

    const handleApply = async (job) => {
        // ✅ 브라우징은 허용, 실제 지원 시에만 로그인 요구
        if (!user) {
            alert(
                '이 공고에 지원하려면 로그인이 필요합니다.\n상단의 "로그인 하러 가기" 버튼을 눌러 먼저 로그인해 주세요.'
            )
            return
        }

        // ✅ [승인 제한] 관리자 승인 전에는 지원 불가
        if (profile?.approval_status !== 'approved') {
            alert(
                '관리자 승인이 완료된 약사님만 지원할 수 있습니다.\n가입 승인 대기 중이므로 조금만 기다려 주세요.'
            )
            return
        }

        if (!window.confirm(`"${job.title}" 공고에 지원하시겠습니까?`)) return

        setApplying(true)
        try {
            const { error: insertError } = await supabase.from('applications').insert({
                job_id: job.id,
                pharmacist_id: user.id,
                status: 'pending',
                pharmacist_name: profile?.display_name || profile?.name || '(이름 없음)',
                contact: profile?.phone || '',
                request_message: null, // Modal doesn't have message input yet
                apply_date: new Date().toISOString()
            })

            if (insertError) {
                if (insertError.code === '23505') {
                    alert('이미 이 공고에 지원하셨습니다.')
                } else {
                    console.error(insertError)
                    alert('지원 처리 중 오류가 발생했습니다.')
                }
                return
            }

            alert('지원이 완료되었습니다.')
            setDetailJob(null) // Close modal on success
        } catch (e) {
            console.error(e)
            alert('지원 처리 중 알 수 없는 오류가 발생했습니다.')
        } finally {
            setApplying(false)
        }
    }


    return (
        <div className="bg-white min-h-full">
            {/* Header / Title Area */}
            <div className="px-4 py-3 border-b bg-white sticky top-0 sm:top-14 z-20 flex flex-col gap-3 shadow-sm">
                <div className="flex justify-between items-center">
                    <h2 className="font-bold text-gray-800 text-lg">채용 공고</h2>
                    <span className="text-xs text-gray-500">총 {filteredJobs.length}건</span>
                </div>

                {/* Search Inputs */}
                <div className="flex flex-col gap-2">
                    <div className="relative">
                        <input
                            className="w-full pl-9 pr-4 py-2 border rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition"
                            placeholder="약국명, 지역, 공고 제목 검색"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                        <svg className="w-4 h-4 text-gray-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>

                    <div className="flex gap-2">
                        {/* Region Filter - Trigger Modal */}
                        <button
                            onClick={() => setIsRegionModalOpen(true)}
                            className={`flex-1 flex items-center justify-between border rounded-lg pl-3 pr-2 py-2 text-sm font-semibold shadow-sm transition h-10
                                ${selectedRegion ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-slate-200 text-slate-700 bg-white'}
                            `}
                        >
                            <span className="truncate">
                                <span className="truncate">
                                    {selectedDistance < 50
                                        ? `${selectedDistance}km 이내`
                                        : selectedRegion
                                            ? `${selectedRegion} ${selectedDistrict && selectedDistrict !== '전체 (구)' ? selectedDistrict : ''}`
                                            : '모든 지역'
                                    }
                                </span>
                            </span>
                            <svg className="w-4 h-4 text-slate-400 flex-none ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </button>

                        <RegionPickerModal
                            isOpen={isRegionModalOpen}
                            onClose={() => setIsRegionModalOpen(false)}
                            onApply={(r, d, dist) => {
                                if (dist) {
                                    setSelectedDistance(dist)
                                    setSelectedRegion('')
                                    setSelectedDistrict('')
                                } else {
                                    setSelectedRegion(r)
                                    setSelectedDistrict(d)
                                    setSelectedDistance(50) // Reset distance
                                }
                            }}
                            initialRegion={selectedRegion}
                            initialDistrict={selectedDistrict}
                            initialDistance={selectedDistance}
                            userAddress={profile?.address}
                        />
                    </div>
                </div>
            </div>

            <div className="p-4 space-y-3 pb-24">
                {loading && <p className="text-center text-gray-400 py-10">공고를 불러오는 중입니다...</p>}

                {!loading && filteredJobs.length === 0 && (
                    <div className="text-center py-10 text-gray-500">
                        <p className="mb-2">검색 결과가 없습니다.</p>
                    </div>
                )}

                {filteredJobs.map((job) => (
                    <div
                        key={job.id}
                        className="border rounded-xl p-4 shadow-sm bg-white hover:border-blue-400 transition cursor-pointer active:scale-[0.99]"
                        onClick={() => setDetailJob(job)}
                    >
                        <div className="flex justify-between items-start mb-2">
                            <div>
                                <h3 className="font-bold text-gray-900 text-sm line-clamp-1">{job.title}</h3>
                                <p className="text-gray-500 text-xs mt-1">{job.location || '약국명 비공개'}</p>
                            </div>
                            <span className="bg-blue-50 text-blue-600 text-[10px] px-2 py-1 rounded-full font-bold">모집중</span>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-gray-700 mb-1">
                            <span className="font-medium text-gray-400">일시</span>
                            <span>{job.date} {job.time ? `(${job.time})` : ''}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-700">
                            <span className="font-medium text-gray-400">지역</span>
                            <span>{job.region} {job.district}</span>
                        </div>

                        {/* Summary for card view */}
                        <div className="mt-3 text-xs text-gray-500 line-clamp-2">
                            {job.description}
                        </div>
                    </div>
                ))}
            </div>

            <JobDetailModal
                isOpen={!!detailJob}
                onClose={() => setDetailJob(null)}
                job={detailJob}
                onApply={handleApply}
                isApplying={applying}
            />
        </div>
    )
}
